import { useState, useEffect, useRef } from 'react';
import { View, Text, Pressable, StyleSheet, Platform, ActivityIndicator, Modal, TextInput, KeyboardAvoidingView } from 'react-native';
import WebScrollView from '../WebScrollView';
import { Calendar, Clock, DollarSign, CheckCircle2, PenTool as Tool, Wrench, HardHat, RefreshCw, ChevronDown, X, Sparkles } from '../../lib/icons';
import { supabase } from '../../lib/supabase';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../../lib/constants';
import { useActiveHome } from '../../contexts/ActiveHomeContext';

type EnrichmentStatus = 'processing' | 'enriched' | 'failed' | null;

interface Task {
  id: string;
  title: string;
  description: string;
  category_id: string | null;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  due_date: string | null;
  completed_at: string | null;
  home_id: string;
  category_name: string;
  skill_level: 'diy' | 'handyman' | 'professional';
  task_type: 'user_task' | 'maintenance_task';
  frequency_months: number | null;
  maintenance_task_id: string | null;
  enrichment_status: EnrichmentStatus;
}

interface TaskEstimate {
  id: string;
  task_id: string;
  time_minutes: number;
  cost_min: number;
  cost_max: number;
  materials_needed: string[];
  tools_needed: string[];
}

interface Home {
  id: string;
  address: string;
  city: string;
  state: string;
}

// Custom Dropdown Picker Component - moved outside to prevent hook order issues
const DropdownPicker = ({ 
  visible, 
  onClose, 
  options, 
  selectedValue, 
  onSelect 
}: { 
  visible: boolean; 
  onClose: () => void; 
  options: Array<{ value: string; label: string }>; 
  selectedValue: string; 
  onSelect: (value: string) => void;
}) => {
  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={pickerStyles.pickerModalOverlay}>
        <Pressable style={pickerStyles.pickerModalBackdrop} onPress={onClose} />
        <View style={pickerStyles.pickerModalContent}>
          <View style={pickerStyles.pickerHeader}>
            <Text style={pickerStyles.pickerHeaderText}>Select Option</Text>
            <Pressable onPress={onClose}>
              <X size={20} color="#6b7280" />
            </Pressable>
          </View>
          <WebScrollView style={pickerStyles.pickerOptions}>
            {options.map((option) => (
              <Pressable
                key={option.value}
                onPress={() => {
                  onSelect(option.value);
                  onClose();
                }}
                style={[
                  pickerStyles.pickerOption,
                  selectedValue === option.value && pickerStyles.pickerOptionSelected
                ]}
              >
                <Text style={[
                  pickerStyles.pickerOptionText,
                  selectedValue === option.value && pickerStyles.pickerOptionTextSelected
                ]}>
                  {option.label}
                </Text>
              </Pressable>
            ))}
          </WebScrollView>
        </View>
      </View>
    </Modal>
  );
};

const HoneyDoListBottomSheet = () => {
  const { homes: accessibleHomes, activeHome, setActiveHomeById, loading: homesLoading } = useActiveHome();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [homes, setHomes] = useState<Home[]>([]);
  const [estimates, setEstimates] = useState<Record<string, TaskEstimate>>({});
  const [loading, setLoading] = useState(true);
  const [selectedHome, setSelectedHome] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [showStatusFilterPicker, setShowStatusFilterPicker] = useState(false);
  const [showHomePicker, setShowHomePicker] = useState(false);
  const [showStatusPicker, setShowStatusPicker] = useState(false);
  const [statusPickerTask, setStatusPickerTask] = useState<Task | null>(null);
  
  // Quick-add task input. The task lands immediately, then AI enriches it
  // in the background so homeowners can add several tasks quickly.
  const [showQuickAddSheet, setShowQuickAddSheet] = useState(false);
  const [quickAddTitle, setQuickAddTitle] = useState('');
  const [quickAddSubmitting, setQuickAddSubmitting] = useState(false);
  const [quickAddError, setQuickAddError] = useState<string | null>(null);
  const quickAddInputRef = useRef<TextInput | null>(null);
  const [enrichingTaskIds, setEnrichingTaskIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setHomes(accessibleHomes as Home[]);
    if (activeHome?.id) {
      setSelectedHome(activeHome.id);
    } else if (!homesLoading) {
      setSelectedHome('');
      setLoading(false);
    }
  }, [accessibleHomes, activeHome, homesLoading]);

  useEffect(() => {
    if (selectedHome) {
      fetchTasks();
    }
  }, [selectedHome, statusFilter]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('combined_tasks')
        .select('*')
        .eq('home_id', selectedHome);

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query
        .order('due_date', { ascending: true })
        .limit(10); // Limit for bottom sheet

      if (error) throw error;
      setTasks(data || []);
      
      // Fetch estimates for user tasks
      const userTaskIds = data
        ?.filter(task => task.task_type === 'user_task')
        .map(task => task.id) || [];
      
      if (userTaskIds.length > 0) {
        fetchEstimates(userTaskIds);
      }

      // For maintenance tasks, create synthetic estimates
      const maintenanceEstimates: Record<string, TaskEstimate> = {};
      data?.filter(task => task.task_type === 'maintenance_task').forEach(task => {
        maintenanceEstimates[task.id] = {
          id: `maintenance-${task.id}`,
          task_id: task.id,
          time_minutes: task.frequency_months ? task.frequency_months * 30 : 60,
          cost_min: 50,
          cost_max: 150,
          materials_needed: ['Varies based on task'],
          tools_needed: ['Standard tools']
        };
      });

      setEstimates(prev => ({...prev, ...maintenanceEstimates}));
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEstimates = async (taskIds: string[]) => {
    try {
      const { data, error } = await supabase
        .from('task_estimates')
        .select('*')
        .in('task_id', taskIds);

      if (error) throw error;

      const estimatesMap: Record<string, TaskEstimate> = {};
      (data || []).forEach(estimate => {
        estimatesMap[estimate.task_id] = estimate;
      });

      setEstimates(prev => ({...prev, ...estimatesMap}));
    } catch (error) {
      console.error('Error fetching estimates:', error);
    }
  };

  const handleStatusChange = async (task: Task, newStatus: Task['status']) => {
    try {
      const updates: any = { 
        status: newStatus 
      };
      
      if (newStatus === 'completed') {
        updates.completed_at = new Date().toISOString();
      } else {
        updates.completed_at = null;
      }
      
      if (task.task_type === 'user_task') {
        const { error } = await supabase
          .from('honeydone_tasks')
          .update(updates)
          .eq('id', task.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('maintenance_plans')
          .update({
            ...updates,
            last_completed_date: newStatus === 'completed' ? new Date().toISOString() : null
          })
          .eq('id', task.id);

        if (error) throw error;
      }
      
      fetchTasks();
    } catch (error) {
      console.error('Error updating task status:', error);
    }
  };

  const openQuickAddSheet = () => {
    setQuickAddTitle('');
    setQuickAddError(null);
    setShowQuickAddSheet(true);
    setTimeout(() => quickAddInputRef.current?.focus(), Platform.OS === 'web' ? 0 : 250);
  };

  const handleCloseQuickAddSheet = () => {
    if (quickAddSubmitting) return;
    setShowQuickAddSheet(false);
    setQuickAddTitle('');
    setQuickAddError(null);
  };

  const resolveDefaultCategoryId = async (): Promise<string | null> => {
    const { data: other } = await supabase
      .from('task_categories')
      .select('id')
      .eq('name', 'Other')
      .maybeSingle();
    if (other?.id) return other.id;

    const { data: fallback } = await supabase
      .from('task_categories')
      .select('id')
      .order('name', { ascending: true })
      .limit(1)
      .maybeSingle();
    return fallback?.id ?? null;
  };

  const resolveTopSortOrder = async (homeId: string): Promise<number> => {
    const { data, error } = await supabase
      .from('combined_tasks')
      .select('sort_order')
      .eq('home_id', homeId)
      .order('sort_order', { ascending: true })
      .limit(1);

    if (error) throw error;
    const min = data?.[0]?.sort_order;
    if (min === undefined || min === null) return 0;
    return min - 1;
  };

  const enrichTaskInBackground = async (taskId: string, rawTitle: string, homeId: string) => {
    try {
      const selectedHomeData = homes.find(h => h.id === homeId);
      const homeContext = selectedHomeData ? {
        address: selectedHomeData.address,
        city: selectedHomeData.city,
        state: selectedHomeData.state
      } : null;

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${SUPABASE_URL}/functions/v1/parse-task`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ taskDescription: rawTitle, homeContext }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Function response error:', errorText);
        throw new Error(`Failed to parse task: ${response.status}`);
      }

      const functionData = await response.json();
      const parsedTask = functionData?.task;
      if (!parsedTask) throw new Error(functionData?.error || 'Failed to parse task');

      let categoryId: string | null = null;
      if (parsedTask.category) {
        const { data: existingCategory } = await supabase
          .from('task_categories')
          .select('id')
          .eq('name', parsedTask.category)
          .maybeSingle();

        if (existingCategory) {
          categoryId = existingCategory.id;
        } else {
          const { data: newCategory } = await supabase
            .from('task_categories')
            .insert({
              name: parsedTask.category,
              skill_level: parsedTask.skill_level
            })
            .select('id')
            .single();
          if (newCategory) categoryId = newCategory.id;
        }
      }

      const updates: Record<string, unknown> = {
        description: parsedTask.description ?? '',
        priority: parsedTask.priority ?? 'normal',
        skill_level: parsedTask.skill_level ?? 'diy',
        due_date: parsedTask.due_date ?? null,
        enrichment_status: 'enriched',
      };
      if (categoryId) updates.category_id = categoryId;

      const { error: updateError } = await supabase
        .from('honeydone_tasks')
        .update(updates)
        .eq('id', taskId);
      if (updateError) throw updateError;

      if (parsedTask.estimated_time_minutes || parsedTask.estimated_cost_min) {
        await supabase
          .from('task_estimates')
          .insert({
            task_id: taskId,
            time_minutes: parsedTask.estimated_time_minutes || 60,
            cost_min: parsedTask.estimated_cost_min || 0,
            cost_max: parsedTask.estimated_cost_max || parsedTask.estimated_cost_min || 0,
            materials_needed: parsedTask.materials_needed || [],
            tools_needed: parsedTask.tools_needed || []
          });
      }
    } catch (error) {
      console.error('Background task enrichment failed:', error);
      await supabase
        .from('honeydone_tasks')
        .update({ enrichment_status: 'failed' })
        .eq('id', taskId);
    } finally {
      setEnrichingTaskIds((prev) => {
        if (!prev.has(taskId)) return prev;
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
      if (selectedHome === homeId) fetchTasks();
    }
  };

  const handleQuickAddSubmit = async () => {
    const title = quickAddTitle.trim();
    if (!title) {
      setQuickAddError('Please type a task');
      return;
    }
    if (!selectedHome) {
      setQuickAddError('Please select a home first');
      return;
    }

    try {
      setQuickAddSubmitting(true);
      setQuickAddError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const [categoryId, sortOrder] = await Promise.all([
        resolveDefaultCategoryId(),
        resolveTopSortOrder(selectedHome)
      ]);

      const { data: newTask, error: taskError } = await supabase
        .from('honeydone_tasks')
        .insert({
          title,
          description: '',
          priority: 'normal',
          skill_level: 'diy',
          category_id: categoryId,
          home_id: selectedHome,
          user_id: user.id,
          status: 'pending',
          sort_order: sortOrder,
          enrichment_status: 'processing',
        })
        .select('id')
        .single();

      if (taskError) throw taskError;
      if (!newTask?.id) throw new Error('Insert returned no id');

      setEnrichingTaskIds((prev) => {
        const next = new Set(prev);
        next.add(newTask.id);
        return next;
      });
      setQuickAddTitle('');
      await fetchTasks();
      void enrichTaskInBackground(newTask.id, title, selectedHome);
      setTimeout(() => quickAddInputRef.current?.focus(), 0);
    } catch (error: any) {
      console.error('Error quick-adding task:', error);
      setQuickAddError(error.message || 'Failed to add task');
    } finally {
      setQuickAddSubmitting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatTime = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  };

  const getPriorityColor = (priority: Task['priority']) => {
    switch (priority) {
      case 'urgent': return styles.priorityUrgent;
      case 'high': return styles.priorityHigh;
      case 'normal': return styles.priorityNormal;
      case 'low': return styles.priorityLow;
      default: return styles.priorityDefault;
    }
  };

  const getPriorityTextColor = (priority: Task['priority']) => {
    switch (priority) {
      case 'urgent': return styles.priorityUrgentText;
      case 'high': return styles.priorityHighText;
      case 'normal': return styles.priorityNormalText;
      case 'low': return styles.priorityLowText;
      default: return styles.priorityDefaultText;
    }
  };

  const getStatusColor = (status: Task['status']) => {
    switch (status) {
      case 'completed': return styles.statusCompleted;
      case 'in_progress': return styles.statusInProgress;
      case 'cancelled': return styles.statusCancelled;
      default: return styles.statusPending;
    }
  };

  const getStatusTextColor = (status: Task['status']) => {
    switch (status) {
      case 'completed': return styles.statusCompletedText;
      case 'in_progress': return styles.statusInProgressText;
      case 'cancelled': return styles.statusCancelledText;
      default: return styles.statusPendingText;
    }
  };

  const getSkillLevelIcon = (skillLevel: string) => {
    switch (skillLevel) {
      case 'diy': return <Tool size={12} color="#059669" />;
      case 'handyman': return <Wrench size={12} color="#2563eb" />;
      case 'professional': return <HardHat size={12} color="#9333ea" />;
      default: return <Tool size={12} color="#6b7280" />;
    }
  };

  const getSkillLevelColor = (skillLevel: string) => {
    switch (skillLevel) {
      case 'diy': return styles.skillDiy;
      case 'handyman': return styles.skillHandyman;
      case 'professional': return styles.skillProfessional;
      default: return styles.skillDefault;
    }
  };

  const getTaskTypeColor = (taskType: string) => {
    return taskType === 'user_task' 
      ? styles.taskTypeUser
      : styles.taskTypeMaintenance;
  };

  const selectedHomeData = homes.find(h => h.id === selectedHome);

  return (
    <View style={styles.container}>
      {/* Header Controls */}
      <View style={styles.headerControls}>
        <View style={styles.headerRow}>
          <View style={styles.filterControls}>
            <Pressable
              onPress={() => setShowStatusFilterPicker(true)}
              style={styles.pickerButton}
            >
              <Text style={styles.pickerButtonText}>
                {statusFilter === 'all' ? 'All' : statusFilter.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
              </Text>
              <ChevronDown size={16} color="#4b5563" />
            </Pressable>
            
            <Pressable
              onPress={fetchTasks}
              style={styles.refreshButton}
            >
              <RefreshCw size={16} color="#4b5563" />
            </Pressable>
          </View>
          
          <Pressable
            onPress={openQuickAddSheet}
            style={styles.addButton}
          >
            <Sparkles size={12} color="#ffffff" />
            <Text style={styles.addButtonText}>Add Task</Text>
          </Pressable>
        </View>

        {homes.length > 1 && (
          <Pressable
            onPress={() => setShowHomePicker(true)}
            style={styles.pickerButton}
          >
            <Text style={styles.pickerButtonText}>
              {selectedHomeData ? `${selectedHomeData.address}, ${selectedHomeData.city}` : 'Select Home'}
            </Text>
            <ChevronDown size={16} color="#4b5563" />
          </Pressable>
        )}
      </View>

      {/* Tasks List */}
      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color="#9ca3af" />
        </View>
      ) : tasks.length === 0 ? (
        <View style={styles.emptyContainer}>
          <CheckCircle2 size={32} color="#9ca3af" />
          <Text style={styles.emptyText}>No tasks found</Text>
        </View>
      ) : (
        <WebScrollView style={styles.tasksList} contentContainerStyle={styles.tasksListContent}>
          {tasks.map((task) => {
            const estimate = estimates[task.id];
            const isEnriching =
              task.task_type === 'user_task' &&
              (enrichingTaskIds.has(task.id) || task.enrichment_status === 'processing');
            
            return (
              <View
                key={task.id}
                style={styles.taskCard}
              >
                <View style={styles.taskHeader}>
                  <View style={styles.taskTitleWrap}>
                    <Text style={styles.taskTitle}>{task.title}</Text>
                    {isEnriching && (
                      <View style={styles.enrichingIndicator}>
                        <ActivityIndicator size="small" color="#6366f1" />
                        <Text style={styles.enrichingText}>Adding details with AI...</Text>
                      </View>
                    )}
                  </View>
                  <View style={styles.taskBadges}>
                    <View style={[styles.badge, getPriorityColor(task.priority)]}>
                      <Text style={[styles.badgeText, getPriorityTextColor(task.priority)]}>
                        {task.priority.charAt(0).toUpperCase()}
                      </Text>
                    </View>
                    <View style={[styles.badge, getStatusColor(task.status)]}>
                      <Text style={[styles.badgeText, getStatusTextColor(task.status)]}>
                        {task.status === 'in_progress' ? 'Progress' : task.status.charAt(0).toUpperCase() + task.status.slice(1)}
                      </Text>
                    </View>
                  </View>
                </View>
                
                {!!task.description && (
                  <Text style={styles.taskDescription} numberOfLines={2}>{task.description}</Text>
                )}
                
                <View style={styles.taskFooter}>
                  <View style={styles.taskMeta}>
                    <View style={[styles.metaBadge, getTaskTypeColor(task.task_type), getSkillLevelColor(task.skill_level)]}>
                      {getSkillLevelIcon(task.skill_level)}
                      <Text style={styles.metaBadgeText}>{task.skill_level.toUpperCase()}</Text>
                    </View>
                    
                    {task.due_date && (
                      <View style={styles.metaItem}>
                        <Calendar size={12} color="#6b7280" />
                        <Text style={styles.metaText}>{new Date(task.due_date).toLocaleDateString()}</Text>
                      </View>
                    )}
                    
                    {estimate && (
                      <>
                        <View style={styles.metaItem}>
                          <Clock size={12} color="#6b7280" />
                          <Text style={styles.metaText}>{formatTime(estimate.time_minutes)}</Text>
                        </View>
                        <View style={styles.metaItem}>
                          <DollarSign size={12} color="#6b7280" />
                          <Text style={styles.metaText}>{formatCurrency(estimate.cost_min)}</Text>
                        </View>
                      </>
                    )}
                  </View>
                  
                  <Pressable
                    onPress={() => {
                      setStatusPickerTask(task);
                      setShowStatusPicker(true);
                    }}
                    style={styles.statusPickerButton}
                  >
                    <Text style={styles.statusPickerText}>
                      {task.status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    </Text>
                    <ChevronDown size={12} color="#4b5563" />
                  </Pressable>
                </View>
              </View>
            );
          })}
        </WebScrollView>
      )}

      {/* Dropdown Pickers */}
      <DropdownPicker
        visible={showStatusFilterPicker}
        onClose={() => setShowStatusFilterPicker(false)}
        options={[
          { value: 'all', label: 'All' },
          { value: 'pending', label: 'Pending' },
          { value: 'in_progress', label: 'In Progress' },
          { value: 'completed', label: 'Completed' }
        ]}
        selectedValue={statusFilter}
        onSelect={setStatusFilter}
      />

      <DropdownPicker
        visible={showHomePicker}
        onClose={() => setShowHomePicker(false)}
        options={homes.map(home => ({ value: home.id, label: `${home.address}, ${home.city}` }))}
        selectedValue={selectedHome}
        onSelect={(value) => {
          setSelectedHome(value);
          setActiveHomeById(value);
        }}
      />

      <DropdownPicker
        visible={showStatusPicker}
        onClose={() => {
          setShowStatusPicker(false);
          setStatusPickerTask(null);
        }}
        options={[
          { value: 'pending', label: 'Pending' },
          { value: 'in_progress', label: 'In Progress' },
          { value: 'completed', label: 'Completed' },
          { value: 'cancelled', label: 'Cancelled' }
        ]}
        selectedValue={statusPickerTask?.status || 'pending'}
        onSelect={(value) => {
          if (statusPickerTask) {
            handleStatusChange(statusPickerTask, value as Task['status']);
          }
          setShowStatusPicker(false);
          setStatusPickerTask(null);
        }}
      />

      {/* Quick Add Task Modal */}
      <Modal
        visible={showQuickAddSheet}
        transparent
        animationType="slide"
        onRequestClose={handleCloseQuickAddSheet}
      >
        <KeyboardAvoidingView
          style={quickAddStyles.modalOverlay}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        >
          <Pressable 
            style={quickAddStyles.modalBackdrop}
            onPress={handleCloseQuickAddSheet}
          />
          <View style={quickAddStyles.sheet}>
            <TextInput
              ref={quickAddInputRef}
              style={quickAddStyles.input}
              placeholder="Add a task"
              placeholderTextColor="#9ca3af"
              value={quickAddTitle}
              onChangeText={(text) => {
                setQuickAddTitle(text);
                if (quickAddError) setQuickAddError(null);
              }}
              onSubmitEditing={handleQuickAddSubmit}
              returnKeyType="done"
              blurOnSubmit={false}
              editable={!quickAddSubmitting}
              autoFocus
            />

            {quickAddError && (
              <Text style={quickAddStyles.errorText}>{quickAddError}</Text>
            )}

            <View style={quickAddStyles.actionRow}>
              <Pressable
                onPress={handleCloseQuickAddSheet}
                style={quickAddStyles.iconButton}
                disabled={quickAddSubmitting}
              >
                <X size={20} color="#6b7280" />
              </Pressable>

              <View style={quickAddStyles.helpTextWrap}>
                <Sparkles size={14} color="#9ca3af" />
                <Text style={quickAddStyles.helpText}>Just type — we'll fill in the rest.</Text>
              </View>
              
              <Pressable
                onPress={handleQuickAddSubmit}
                style={[
                  quickAddStyles.saveButton,
                  (quickAddSubmitting || !quickAddTitle.trim()) && quickAddStyles.saveButtonDisabled
                ]}
                disabled={quickAddSubmitting || !quickAddTitle.trim()}
              >
                {quickAddSubmitting ? (
                  <ActivityIndicator size="small" color="#1d4ed8" />
                ) : (
                  <Text style={quickAddStyles.saveButtonText}>Save</Text>
                )}
              </Pressable>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 24,
    gap: 16,
  },
  headerControls: {
    gap: 12,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  filterControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  pickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    backgroundColor: '#ffffff',
    minWidth: 100,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  pickerButtonText: {
    fontSize: 14,
    color: '#111827',
  },
  refreshButton: {
    padding: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#000000',
    borderRadius: 6,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  addButtonText: {
    fontSize: 14,
    color: '#ffffff',
    fontWeight: '500',
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 32,
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 32,
    gap: 8,
  },
  emptyText: {
    fontSize: 14,
    color: '#4b5563',
  },
  tasksList: {
    flex: 1,
  },
  tasksListContent: {
    gap: 12,
  },
  taskCard: {
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    padding: 16,
    gap: 8,
  },
  taskHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
  },
  taskTitleWrap: {
    flex: 1,
    gap: 4,
  },
  taskTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
  },
  enrichingIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  enrichingText: {
    fontSize: 11,
    color: '#6b7280',
    fontStyle: 'italic',
  },
  taskBadges: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  badge: {
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 4,
  },
  badgeText: {
    fontSize: 10,
    fontWeight: '500',
  },
  priorityUrgent: {
    backgroundColor: '#fee2e2',
  },
  priorityUrgentText: {
    color: '#dc2626',
  },
  priorityHigh: {
    backgroundColor: '#fed7aa',
  },
  priorityHighText: {
    color: '#ea580c',
  },
  priorityNormal: {
    backgroundColor: '#dbeafe',
  },
  priorityNormalText: {
    color: '#2563eb',
  },
  priorityLow: {
    backgroundColor: '#dcfce7',
  },
  priorityLowText: {
    color: '#16a34a',
  },
  priorityDefault: {
    backgroundColor: '#f3f4f6',
  },
  priorityDefaultText: {
    color: '#4b5563',
  },
  statusCompleted: {
    backgroundColor: '#dcfce7',
  },
  statusCompletedText: {
    color: '#16a34a',
  },
  statusInProgress: {
    backgroundColor: '#dbeafe',
  },
  statusInProgressText: {
    color: '#2563eb',
  },
  statusCancelled: {
    backgroundColor: '#f3f4f6',
  },
  statusCancelledText: {
    color: '#4b5563',
  },
  statusPending: {
    backgroundColor: '#fed7aa',
  },
  statusPendingText: {
    color: '#ea580c',
  },
  taskDescription: {
    fontSize: 12,
    color: '#4b5563',
    lineHeight: 16,
  },
  taskFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  taskMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  metaBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 4,
  },
  metaBadgeText: {
    fontSize: 10,
    color: '#111827',
    fontWeight: '500',
  },
  skillDiy: {
    backgroundColor: '#dcfce7',
  },
  skillHandyman: {
    backgroundColor: '#dbeafe',
  },
  skillProfessional: {
    backgroundColor: '#f3e8ff',
  },
  skillDefault: {
    backgroundColor: '#f3f4f6',
  },
  taskTypeUser: {
    backgroundColor: '#eef2ff',
  },
  taskTypeMaintenance: {
    backgroundColor: '#fef3c7',
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metaText: {
    fontSize: 10,
    color: '#6b7280',
  },
  statusPickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 4,
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  statusPickerText: {
    fontSize: 10,
    color: '#111827',
  },
});

// Separate styles for DropdownPicker component
const pickerStyles = StyleSheet.create({
  pickerModalOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  pickerModalBackdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  pickerModalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: '50%',
  },
  pickerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  pickerHeaderText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  pickerOptions: {
    maxHeight: 300,
  },
  pickerOption: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  pickerOptionSelected: {
    backgroundColor: '#f3f4f6',
  },
  pickerOptionText: {
    fontSize: 16,
    color: '#111827',
  },
  pickerOptionTextSelected: {
    fontWeight: '500',
    color: '#000000',
  },
});

// Google-Tasks-style quick-add sheet.
const quickAddStyles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalBackdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  sheet: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingTop: 20,
    paddingHorizontal: 20,
    paddingBottom: 12,
    gap: 12,
  },
  input: {
    fontSize: 16,
    color: '#111827',
    paddingVertical: 8,
    paddingHorizontal: 0,
    backgroundColor: 'transparent',
    ...(Platform.OS === 'web' && ({
      outlineStyle: 'none',
      border: 'none',
    } as any)),
  },
  errorText: {
    fontSize: 12,
    color: '#dc2626',
  },
  actionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#f3f4f6',
  },
  iconButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  helpTextWrap: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  helpText: {
    fontSize: 12,
    color: '#9ca3af',
    flexShrink: 1,
  },
  saveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    minWidth: 64,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  saveButtonDisabled: {
    opacity: 0.4,
    ...(Platform.OS === 'web' && ({
      cursor: 'not-allowed',
    } as any)),
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1d4ed8',
  },
});

export default HoneyDoListBottomSheet;
